import { SkyBadge, SkyText, type SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import {
  NOTIFY_SUBJECT_LABELS,
  SUBJECT_HELP,
  type NotifySubject,
} from "@shared/types/notifications";
import { cellKey, type RuleDraft, type SubjectStatus } from "@/lib/notifications/rulesDraft";
import { NotificationRuleFieldset } from "./NotificationRuleFieldset";

/** A status is said by its word; the colour only repeats it (never colour alone). */
const STATUS_COLORS: Record<SubjectStatus, SkyBadgeColor> = {
  active: "success",
  partial: "warning",
  no_recipient: "warning",
  off: "subtle",
};

/**
 * A subject section's header (round 3, owner 2026-09-30): the subject, how many of its rules are
 * on, and its status as a tinted badge with its word. It is the accordion trigger's content, so
 * it is on screen whether the section is open or closed.
 */
export function NotificationSubjectHeading({
  subject,
  summary,
  needsFix = false,
}: {
  subject: NotifySubject;
  summary: { label: string; status: SubjectStatus; words: string };
  /** A rule of this subject has an address to fix (review S2): said in the header, open or closed. */
  needsFix?: boolean;
}) {
  return (
    <span
      className="flex flex-wrap items-center gap-x-3 gap-y-1"
      data-testid={`notification-section-${subject}`}
    >
      {/* Round 7 T-37: a sub-section heading (the accordion's h3), 14 px like every other; it was
          16 px, the only section tab that size. */}
      <SkyText as="span" size={300} weight="semibold">
        {NOTIFY_SUBJECT_LABELS[subject]}
      </SkyText>
      <SkyText as="span" size={200} color="muted">
        {summary.label}
      </SkyText>
      <SkyBadge
        appearance="tint"
        color={STATUS_COLORS[summary.status]}
        testId={`notification-section-status-${subject}`}
      >
        {summary.words}
      </SkyBadge>
      {needsFix ? (
        <SkyBadge appearance="tint" color="danger" testId={`notification-section-fix-${subject}`}>
          Needs a fix
        </SkyBadge>
      ) : null}
    </span>
  );
}

/**
 * "What: Imports" (and Transformations, Schedules, Credentials, Data health): one subject's three
 * rules (contract-notif R10, R14, §3.7), the body of its collapsible section. A `fieldset` whose
 * legend "What: Imports" is read by a screen reader (the section's header shows the subject), and
 * the subject's help sentence, so nobody is misled about where a transformation counts: one that
 * runs inside an import or a schedule belongs to that import or schedule. No card of its own
 * (round 3: "too many boxes"): the rules are divided by a rule line.
 */
export function NotificationSubjectSection({
  subject,
  rules,
  readOnly,
  emailsMasked,
  fieldErrors,
  copyStatus,
  copyHelpCell = null,
  hasWebhook,
  onRuleChange,
  onCopy,
}: {
  subject: NotifySubject;
  /** This subject's three rules, in failure, warning, success order. */
  rules: RuleDraft[];
  readOnly: boolean;
  emailsMasked: boolean;
  fieldErrors: Record<string, string>;
  copyStatus: { cell: string; text: string } | null;
  /** D14: the one rule (a cell key) whose copy sentence is on screen; null shows none. */
  copyHelpCell?: string | null;
  /** #2522: whether the Teams switch reaches anyone (the instance has a webhook). */
  hasWebhook: boolean;
  onRuleChange: (next: RuleDraft) => void;
  onCopy: (rule: RuleDraft) => void;
}) {
  return (
    <fieldset className="min-w-0" data-testid={`notification-subject-${subject}`}>
      <legend className="sr-only">What: {NOTIFY_SUBJECT_LABELS[subject]}</legend>
      {/* Round 6 walk W24: space under the help, which touched the first rule's switch. */}
      <SkyText
        as="p"
        size={200}
        color="muted"
        block
        className="mb-2"
        testId={`notification-subject-help-${subject}`}
      >
        {SUBJECT_HELP[subject]}
      </SkyText>
      <div className="flex flex-col divide-y divide-border">
        {rules.map((rule) => {
          const key = cellKey(rule.subject, rule.outcome);
          return (
            <NotificationRuleFieldset
              key={key}
              rule={rule}
              readOnly={readOnly}
              emailsMasked={emailsMasked}
              fieldError={fieldErrors[key] ?? null}
              copyStatus={copyStatus?.cell === key ? copyStatus.text : null}
              copyHelpShown={copyHelpCell === key}
              hasWebhook={hasWebhook}
              onChange={onRuleChange}
              onCopy={() => onCopy(rule)}
            />
          );
        })}
      </div>
    </fieldset>
  );
}
